/**
 * 학생 매칭 페이지 — 진학사 스타일 9칸 시각화.
 *
 * 입력: 백분위 평균(정시) 또는 내신 등급(수시)
 * 출력: 9칸 게이지 + 칸별 학과 그룹 (배터리 모양)
 */
import React, { useEffect, useState } from 'react';
import hqAdmissionService, { MatchResult, MatchResponse } from '../services/hqAdmissionService';

type Mode = '정시' | '학생부교과' | '학생부종합';
type InputMode = 'simple' | 'detailed'; // 간단 백분위 1개 vs 영역별 점수

const HqAdmissionMatchPage: React.FC = () => {
  const [mode, setMode] = useState<Mode>('정시');
  const [inputMode, setInputMode] = useState<InputMode>('detailed');
  const [percentile, setPercentile] = useState<string>('87');
  const [grade, setGrade] = useState<string>('2.5');
  const [gun, setGun] = useState<string>('');
  const [strategy, setStrategy] = useState<'3y' | '5y' | '2025'>('3y');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<MatchResponse | null>(null);
  const [evaluated, setEvaluated] = useState<Record<string, any> | null>(null);
  const [computedAvg, setComputedAvg] = useState<{ percentile: number; grade: number | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeKan, setActiveKan] = useState<number | null>(null);
  const [activeGroup, setActiveGroup] = useState<'stable' | 'main' | 'challenge' | null>(null);

  // 영역별 입력
  const [korean, setKorean] = useState<string>('130');
  const [math, setMath] = useState<string>('132');
  const [english, setEnglish] = useState<string>('3');
  const [inq1Subject, setInq1Subject] = useState<string>('사회탐구_생활과 윤리');
  const [inq1Score, setInq1Score] = useState<string>('65');
  const [inq2Subject, setInq2Subject] = useState<string>('사회탐구_윤리와 사상');
  const [inq2Score, setInq2Score] = useState<string>('67');
  const [hist, setHist] = useState<string>('2');

  const [subjects, setSubjects] = useState<{ key: string; subject: string; type: string }[]>([]);

  useEffect(() => {
    hqAdmissionService.listSubjects().then(d => setSubjects(d.subjects)).catch(() => {});
  }, []);

  const inquirySubjects = subjects.filter(s => s.subject.includes('탐구'));

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setActiveKan(null);
    setEvaluated(null);
    setComputedAvg(null);
    try {
      let r: MatchResponse;
      if (mode === '정시' && inputMode === 'detailed') {
        // 영역별 점수
        const body: any = { strategy, gun: gun || undefined, limit: 2000 };
        if (korean) body.korean_score = parseInt(korean);
        if (math) body.math_score = parseInt(math);
        if (english) body.english_grade = parseInt(english);
        if (inq1Subject && inq1Score) {
          body.inquiry1_subject = inq1Subject;
          body.inquiry1_score = parseInt(inq1Score);
        }
        if (inq2Subject && inq2Score) {
          body.inquiry2_subject = inq2Subject;
          body.inquiry2_score = parseInt(inq2Score);
        }
        if (hist) body.korean_history_grade = parseInt(hist);

        const resp = await hqAdmissionService.matchByScores(body);
        if ((resp as any).error) throw new Error((resp as any).error);
        r = resp.match;
        setEvaluated(resp.evaluated);
        setComputedAvg({ percentile: resp.average_percentile, grade: resp.average_grade });
      } else if (mode === '정시') {
        const p = parseFloat(percentile);
        if (isNaN(p) || p < 0 || p > 100) throw new Error('백분위 0~100 사이로 입력');
        r = await hqAdmissionService.matchJeongsi({
          percentile: p,
          strategy,
          gun: gun || undefined,
          limit: 2000,
        });
      } else {
        const g = parseFloat(grade);
        if (isNaN(g) || g < 1 || g > 9) throw new Error('내신 1.0~9.0 사이로 입력');
        r = await hqAdmissionService.matchSusi({
          grade: g,
          jeonhyeong: mode,
          strategy,
          limit: 2000,
        });
      }
      setResult(r);
    } catch (e: any) {
      setError(e.message || e.response?.data?.detail || '매칭 실패');
    } finally {
      setLoading(false);
    }
  };

  const groupKans: Record<NonNullable<typeof activeGroup>, number[]> = {
    stable: [7, 8],
    main: [5, 6],
    challenge: [3, 4],
  };

  const filtered = result?.results.filter(r => {
    if (activeKan !== null) return r.kan === activeKan;
    if (activeGroup) return groupKans[activeGroup].includes(r.kan ?? 0);
    return true;
  }) || [];

  const handleGroupClick = (g: NonNullable<typeof activeGroup>) => {
    setActiveKan(null);
    setActiveGroup(activeGroup === g ? null : g);
  };

  const handleKanClick = (k: number) => {
    setActiveGroup(null);
    setActiveKan(activeKan === k ? null : k);
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-2">🎯 학생 매칭 (진학사 9칸 시스템)</h1>
      <p className="text-sm text-slate-600 mb-6">
        진학사 합격예측 칸수 시스템 (1칸=불가, 9칸=거의확정)
      </p>

      {/* 입력 폼 */}
      <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
        <div className="flex gap-2 mb-4">
          {(['정시', '학생부교과', '학생부종합'] as Mode[]).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`px-4 py-2 rounded ${
                mode === m
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {m}
            </button>
          ))}
        </div>

        {/* 정시일 때 입력 모드 토글 */}
        {mode === '정시' && (
          <div className="flex gap-2 mb-4 text-sm">
            <button
              onClick={() => setInputMode('detailed')}
              className={`px-3 py-1.5 rounded ${inputMode === 'detailed' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700'}`}
            >
              📝 영역별 점수 입력 (정밀)
            </button>
            <button
              onClick={() => setInputMode('simple')}
              className={`px-3 py-1.5 rounded ${inputMode === 'simple' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700'}`}
            >
              ⚡ 백분위 평균만 (간단)
            </button>
          </div>
        )}

        {/* 정시 영역별 입력 */}
        {mode === '정시' && inputMode === 'detailed' ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-slate-600 mb-1">국어 표준점수 (47~147)</label>
                <input type="number" min={47} max={147} value={korean} onChange={(e) => setKorean(e.target.value)} className="w-full px-3 py-2 border rounded" placeholder="130" />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">수학 표준점수 (61~139)</label>
                <input type="number" min={61} max={139} value={math} onChange={(e) => setMath(e.target.value)} className="w-full px-3 py-2 border rounded" placeholder="132" />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">영어 등급 (1~9)</label>
                <input type="number" min={1} max={9} value={english} onChange={(e) => setEnglish(e.target.value)} className="w-full px-3 py-2 border rounded" placeholder="3" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="block text-xs text-slate-600 mb-1">탐구1 과목</label>
                  <select value={inq1Subject} onChange={(e) => setInq1Subject(e.target.value)} className="w-full px-3 py-2 border rounded text-sm">
                    <option value="">선택</option>
                    {inquirySubjects.map(s => <option key={s.key} value={s.key}>{s.subject} {s.type}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">표준점수</label>
                  <input type="number" value={inq1Score} onChange={(e) => setInq1Score(e.target.value)} className="w-full px-3 py-2 border rounded" placeholder="65" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="block text-xs text-slate-600 mb-1">탐구2 과목</label>
                  <select value={inq2Subject} onChange={(e) => setInq2Subject(e.target.value)} className="w-full px-3 py-2 border rounded text-sm">
                    <option value="">선택</option>
                    {inquirySubjects.map(s => <option key={s.key} value={s.key}>{s.subject} {s.type}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">표준점수</label>
                  <input type="number" value={inq2Score} onChange={(e) => setInq2Score(e.target.value)} className="w-full px-3 py-2 border rounded" placeholder="67" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-slate-600 mb-1">한국사 등급 (1~9)</label>
                <input type="number" min={1} max={9} value={hist} onChange={(e) => setHist(e.target.value)} className="w-full px-3 py-2 border rounded" placeholder="2" />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">군 필터</label>
                <select value={gun} onChange={(e) => setGun(e.target.value)} className="w-full px-3 py-2 border rounded">
                  <option value="">전체</option>
                  <option value="가군">가군</option>
                  <option value="나군">나군</option>
                  <option value="다군">다군</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">매칭 기준</label>
                <select value={strategy} onChange={(e) => setStrategy(e.target.value as any)} className="w-full px-3 py-2 border rounded">
                  <option value="3y">3년 평균 ⭐</option>
                  <option value="5y">5년 평균</option>
                  <option value="2025">2025 단독</option>
                </select>
              </div>
            </div>
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {mode === '정시' ? (
            <>
              <div>
                <label className="block text-sm text-slate-600 mb-1">학생 백분위 평균 (국·수·탐)</label>
                <input
                  type="number"
                  step="0.1"
                  min={0}
                  max={100}
                  value={percentile}
                  onChange={(e) => setPercentile(e.target.value)}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="87.0"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-600 mb-1">군 필터</label>
                <select
                  value={gun}
                  onChange={(e) => setGun(e.target.value)}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value="">전체</option>
                  <option value="가군">가군</option>
                  <option value="나군">나군</option>
                  <option value="다군">다군</option>
                </select>
              </div>
            </>
          ) : (
            <div>
              <label className="block text-sm text-slate-600 mb-1">평균 내신등급 (1.0~9.0)</label>
              <input
                type="number"
                step="0.1"
                min={1}
                max={9}
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                className="w-full px-3 py-2 border rounded"
                placeholder="2.5"
              />
            </div>
          )}

          <div>
            <label className="block text-sm text-slate-600 mb-1">매칭 기준</label>
            <select
              value={strategy}
              onChange={(e) => setStrategy(e.target.value as '3y' | '5y' | '2025')}
              className="w-full px-3 py-2 border rounded"
            >
              <option value="3y">3년 평균 (2023~2025) ⭐ 추천</option>
              <option value="5y">5년 평균</option>
              <option value="2025">2025 단독</option>
            </select>
          </div>
        </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={loading}
          className="mt-4 px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? '매칭 중...' : '🔍 매칭'}
        </button>

        {error && <div className="mt-3 text-red-600 text-sm">⚠️ {error}</div>}
      </div>

      {/* 영역별 환산 결과 (영역별 입력 시) */}
      {evaluated && computedAvg && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-semibold text-indigo-900">📊 학생 점수 자동 환산</h3>
            <div className="text-right">
              <div className="text-xs text-indigo-700">평균 백분위</div>
              <div className="text-2xl font-bold text-indigo-700">{computedAvg.percentile}</div>
              {computedAvg.grade && <div className="text-xs text-indigo-600">평균 {computedAvg.grade}등급</div>}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
            {Object.entries(evaluated).map(([k, v]: [string, any]) => (
              <div key={k} className="bg-white p-2 rounded">
                <div className="text-xs text-slate-500">{k}</div>
                {v.percentile !== undefined ? (
                  <div className="text-sm">
                    <span className="font-semibold">{v.subject} {v.type}</span>
                    <div className="text-xs text-slate-600">
                      표준 {v.score} → 백분위 <span className="font-bold text-indigo-700">{v.percentile}</span> ({v.grade}등급)
                    </div>
                  </div>
                ) : (
                  <div className="text-sm">
                    <span className="font-semibold">{v.type || '-'}</span>
                    <div className="text-xs text-slate-600"><span className="font-bold">{v.grade}등급</span></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 결과 — 9칸 게이지 */}
      {result && (
        <>
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">
                📊 9칸 게이지 — 총 {result.total_matched.toLocaleString()}건
              </h2>
              {(activeKan !== null || activeGroup !== null) && (
                <button
                  onClick={() => { setActiveKan(null); setActiveGroup(null); }}
                  className="text-sm text-blue-600 hover:underline"
                >
                  전체 보기 ←
                </button>
              )}
            </div>

            {/* 가로 9칸 카드 배치 */}
            <div className="grid grid-cols-9 gap-2">
              {[9, 8, 7, 6, 5, 4, 3, 2, 1].map((k) => {
                const count = result.by_kan?.[String(k)] ?? 0;
                const meta = KAN_META[k];
                const max = Math.max(...Object.values(result.by_kan || {}), 1);
                const heightPct = max > 0 ? Math.max((count / max) * 100, 5) : 5;
                const isActive = activeKan === k;

                return (
                  <button
                    key={k}
                    disabled={count === 0}
                    onClick={() => handleKanClick(k)}
                    className={`relative flex flex-col items-stretch rounded-lg border-2 overflow-hidden transition ${
                      count === 0
                        ? 'opacity-30 cursor-not-allowed border-slate-200'
                        : isActive
                          ? 'border-blue-500 ring-2 ring-blue-300 shadow-md'
                          : 'border-slate-200 hover:border-slate-400 hover:shadow'
                    }`}
                  >
                    {/* 상단: 칸수 + 확률 */}
                    <div className={`px-2 py-1.5 ${meta.bgClass} text-center`}>
                      <div className="text-base font-bold leading-tight">{k}칸</div>
                      <div className="text-[10px] opacity-80">{meta.probability}%</div>
                    </div>

                    {/* 중간: 건수 막대 (높이 비례) */}
                    <div className="relative bg-slate-50 h-16 flex items-end">
                      <div
                        className={`w-full ${meta.barClass} transition-all`}
                        style={{ height: `${heightPct}%` }}
                      />
                    </div>

                    {/* 하단: 건수 + 라벨 */}
                    <div className="px-1 py-1.5 bg-white text-center border-t">
                      <div className="text-sm font-bold">{count.toLocaleString()}</div>
                      <div className={`text-[10px] truncate ${meta.textClass}`}>{meta.label}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* 컨설턴트 가이드 (클릭 가능) */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
              <button
                onClick={() => handleGroupClick('stable')}
                className={`text-left p-3 rounded transition border-2 ${
                  activeGroup === 'stable'
                    ? 'bg-green-100 border-green-500 ring-2 ring-green-300'
                    : 'bg-green-50 border-transparent hover:border-green-300'
                }`}
              >
                <div className="font-semibold text-green-700">✅ 안정 카드 (7~8칸)</div>
                <div className="text-xs text-green-600 mt-1">
                  안전하게 합격할 학과 1~2개. {(result.by_kan?.['7'] ?? 0) + (result.by_kan?.['8'] ?? 0)}개 후보
                </div>
                <div className="text-xs text-green-700 mt-1 font-medium">
                  {activeGroup === 'stable' ? '✓ 선택됨 — 클릭하여 해제' : '클릭 →'}
                </div>
              </button>
              <button
                onClick={() => handleGroupClick('main')}
                className={`text-left p-3 rounded transition border-2 ${
                  activeGroup === 'main'
                    ? 'bg-yellow-100 border-yellow-500 ring-2 ring-yellow-300'
                    : 'bg-yellow-50 border-transparent hover:border-yellow-300'
                }`}
              >
                <div className="font-semibold text-yellow-700">⭐ 메인 (5~6칸)</div>
                <div className="text-xs text-yellow-700 mt-1">
                  커트라인 적정. {(result.by_kan?.['5'] ?? 0) + (result.by_kan?.['6'] ?? 0)}개 후보
                </div>
                <div className="text-xs text-yellow-700 mt-1 font-medium">
                  {activeGroup === 'main' ? '✓ 선택됨 — 클릭하여 해제' : '클릭 →'}
                </div>
              </button>
              <button
                onClick={() => handleGroupClick('challenge')}
                className={`text-left p-3 rounded transition border-2 ${
                  activeGroup === 'challenge'
                    ? 'bg-orange-100 border-orange-500 ring-2 ring-orange-300'
                    : 'bg-orange-50 border-transparent hover:border-orange-300'
                }`}
              >
                <div className="font-semibold text-orange-700">🚀 상향 도전 (3~4칸)</div>
                <div className="text-xs text-orange-700 mt-1">
                  도전 카드. {(result.by_kan?.['3'] ?? 0) + (result.by_kan?.['4'] ?? 0)}개 후보
                </div>
                <div className="text-xs text-orange-700 mt-1 font-medium">
                  {activeGroup === 'challenge' ? '✓ 선택됨 — 클릭하여 해제' : '클릭 →'}
                </div>
              </button>
            </div>
          </div>

          {/* 학과 리스트 */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b flex justify-between items-center">
              <div className="font-semibold">
                {activeKan !== null
                  ? `🎯 ${activeKan}칸 학과 리스트 (${KAN_META[activeKan]?.label})`
                  : activeGroup === 'stable'
                    ? '✅ 안정 카드 (7~8칸 학과)'
                    : activeGroup === 'main'
                      ? '⭐ 메인 (5~6칸 학과 — 커트라인 적정)'
                      : activeGroup === 'challenge'
                        ? '🚀 상향 도전 (3~4칸 학과)'
                        : '전체 학과'}
              </div>
              <div className="text-sm text-slate-600">
                {filtered.length.toLocaleString()}건 / 상위 100건 표시
              </div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left">칸</th>
                  <th className="px-3 py-2 text-left">합격%</th>
                  <th className="px-3 py-2 text-left">대학</th>
                  <th className="px-3 py-2 text-left">학과</th>
                  <th className="px-3 py-2 text-left">군</th>
                  <th className="px-3 py-2 text-right">합격선</th>
                  <th className="px-3 py-2 text-right">차이</th>
                  <th className="px-3 py-2 text-right">모집</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 100).map((r, i) => (
                  <ResultRow key={i} row={r} mode={mode} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// 9칸 메타정보
// ─────────────────────────────────────────────────────────
const KAN_META: Record<number, {
  label: string;
  probability: number;
  bgClass: string;
  barClass: string;
  textClass: string;
}> = {
  9: { label: '최상위 안정', probability: 99, bgClass: 'bg-blue-100 text-blue-800', barClass: 'bg-blue-500', textClass: 'text-blue-700' },
  8: { label: '안정 (상위)', probability: 96, bgClass: 'bg-blue-100 text-blue-800', barClass: 'bg-blue-400', textClass: 'text-blue-700' },
  7: { label: '안정', probability: 92, bgClass: 'bg-green-100 text-green-800', barClass: 'bg-green-500', textClass: 'text-green-700' },
  6: { label: '안정/적정', probability: 80, bgClass: 'bg-green-100 text-green-800', barClass: 'bg-green-400', textClass: 'text-green-700' },
  5: { label: '적정 (커트)', probability: 68, bgClass: 'bg-yellow-100 text-yellow-800', barClass: 'bg-yellow-500', textClass: 'text-yellow-700' },
  4: { label: '적정/도전', probability: 40, bgClass: 'bg-orange-100 text-orange-800', barClass: 'bg-orange-400', textClass: 'text-orange-700' },
  3: { label: '도전', probability: 20, bgClass: 'bg-orange-100 text-orange-800', barClass: 'bg-orange-500', textClass: 'text-orange-700' },
  2: { label: '상향 도전', probability: 5, bgClass: 'bg-red-100 text-red-800', barClass: 'bg-red-400', textClass: 'text-red-700' },
  1: { label: '사실상 불가', probability: 1, bgClass: 'bg-red-100 text-red-800', barClass: 'bg-red-500', textClass: 'text-red-700' },
};

// 배터리 칸수 시각화
const KanBattery: React.FC<{ kan: number }> = ({ kan }) => {
  const meta = KAN_META[kan];
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
        <div
          key={i}
          className={`w-2 h-5 rounded-sm ${
            i <= kan ? meta.barClass : 'bg-slate-200'
          }`}
        />
      ))}
    </div>
  );
};

// 결과 행
const ResultRow: React.FC<{ row: MatchResult; mode: Mode }> = ({ row, mode }) => {
  const cut =
    mode === '정시'
      ? row.p70_avg_3y ?? row.p70_avg_5y ?? row.p70_avg_2025
      : row.cut70_grade_3y ?? row.cut70_grade_5y ?? row.cut70_grade_2025;
  const meta = row.kan ? KAN_META[row.kan] : null;

  return (
    <tr className="border-t hover:bg-slate-50">
      <td className="px-3 py-2">
        {row.kan ? <KanBattery kan={row.kan} /> : '-'}
      </td>
      <td className="px-3 py-2">
        {meta && (
          <span className={`text-xs font-semibold ${meta.textClass}`}>
            {meta.probability}%
          </span>
        )}
      </td>
      <td className="px-3 py-2 font-medium">{row.univ}</td>
      <td className="px-3 py-2">{row.dept}</td>
      <td className="px-3 py-2 text-slate-600 text-xs">{row.gun || '-'}</td>
      <td className="px-3 py-2 text-right tabular-nums">{cut?.toFixed(2) ?? '-'}</td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-600">
        {row.diff > 0 ? '+' : ''}{row.diff.toFixed(1)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-500">
        {row.capacity_2025 ?? '-'}
      </td>
    </tr>
  );
};

export default HqAdmissionMatchPage;
